// Conway's Game of Life as a generator.
// Integer state for cross-architecture determinism. Time-stepped from an
// initial random fill. Wraps around edges (toroidal).

import type { LayerSpec } from '@/core/pipeline';
import { mulberry32 } from '@/lib/rng';
import { parseHex, type RGB } from '@/lib/color';

type CellularParams = {
  density: number;   // initial fill density 0..1
  alive: string;     // hex
  dead: string;      // hex
};

// Memo across renders: keyed by (seed, time, density, w, h). Since the pipeline
// re-uses the same module per layer, this lets us avoid recomputing earlier
// generations when the time slider only nudges forward.
type CacheEntry = { state: Uint8Array; gen: number };
const cache = new Map<string, CacheEntry>();
const MAX_CACHE = 16;

function gcCache(): void {
  while (cache.size > MAX_CACHE) {
    const k = cache.keys().next().value;
    if (k !== undefined) cache.delete(k);
  }
}

export const cellularSpec: LayerSpec = {
  kind: 'cellular',
  category: 'generator',
  animated: true,
  label: 'Game of Life',
  defaults: {
    density: 0.35,
    alive: '#ededed',
    dead:  '#0a0a0a',
  } satisfies CellularParams,
  schema: {
    density: { type: 'number', label: 'Init density', min: 0.05, max: 0.9, step: 0.01, default: 0.35 },
    alive:   { type: 'color',  label: 'Alive', default: '#ededed' },
    dead:    { type: 'color',  label: 'Dead',  default: '#0a0a0a' },
  },
  run: (_input, params, ctx) => {
    const p = params as CellularParams;
    const w = ctx.width, h = ctx.height;
    const target = Math.max(0, Math.floor(ctx.time));
    const key = `${ctx.seed}|${p.density}|${w}x${h}`;
    let entry = cache.get(key);
    if (!entry || entry.gen > target) {
      entry = { state: seedFill(ctx.seed, p.density, w, h), gen: 0 };
      cache.set(key, entry);
      gcCache();
    }
    while (entry.gen < target) {
      entry.state = step(entry.state, w, h);
      entry.gen++;
    }
    return paint(entry.state, w, h, p.alive, p.dead);
  },
};

function seedFill(seed: number, density: number, w: number, h: number): Uint8Array {
  const rng = mulberry32(seed);
  const out = new Uint8Array(w * h);
  for (let i = 0; i < out.length; i++) out[i] = rng() < density ? 1 : 0;
  return out;
}

function step(prev: Uint8Array, w: number, h: number): Uint8Array {
  const next = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    const ym = (y - 1 + h) % h;
    const yp = (y + 1) % h;
    for (let x = 0; x < w; x++) {
      const xm = (x - 1 + w) % w;
      const xp = (x + 1) % w;
      const n =
        (prev[ym * w + xm] as number) + (prev[ym * w + x] as number) + (prev[ym * w + xp] as number) +
        (prev[y  * w + xm] as number)                                  + (prev[y  * w + xp] as number) +
        (prev[yp * w + xm] as number) + (prev[yp * w + x] as number) + (prev[yp * w + xp] as number);
      const alive = prev[y * w + x] as number;
      next[y * w + x] = alive ? (n === 2 || n === 3 ? 1 : 0) : (n === 3 ? 1 : 0);
    }
  }
  return next;
}

function paint(state: Uint8Array, w: number, h: number, aliveHex: string, deadHex: string): ImageData {
  const A = parseHex(aliveHex) ?? ([235, 235, 235] as RGB);
  const D = parseHex(deadHex) ?? ([10, 10, 10] as RGB);
  const out = new ImageData(w, h);
  const data = out.data;
  for (let i = 0; i < state.length; i++) {
    const c = state[i] === 1 ? A : D;
    const j = i * 4;
    data[j] = c[0]; data[j + 1] = c[1]; data[j + 2] = c[2]; data[j + 3] = 255;
  }
  return out;
}
